import { cx } from "../../lib/cx"

/**
 * The Loomina identity.
 *
 * The mark is the product in one glyph: three lines of a list, the top one
 * struck through and closed, the bottom one still an open ring. That is the
 * whole idea - a chain of questions where you can see at a glance which are
 * answered and which are not.
 *
 * The wordmark is drawn as geometric monoline letterforms rather than set in a
 * webfont. A logo that depends on a font renders differently while the font
 * loads, differently again if it fails, and differently in an email client or
 * a favicon. Paths render identically everywhere, at any size, offline.
 *
 * The letters take `currentColor`, so the same component works on the light
 * page and on the dark sidebar without a second copy.
 */

/* -------------------------------------------------------------------------- */
/* Mark                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The tile. `flat` drops the gradient and the tile, leaving the glyph alone in
 * `currentColor` - which is what a favicon-sized rendering and a monochrome
 * print both want.
 */
export function LoominaMark({ size = 28, className, title, idPrefix = "loomina" }) {
	const gradient = `${idPrefix}-tile`

	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 32 32"
			fill="none"
			role={title ? "img" : "presentation"}
			aria-hidden={title ? undefined : true}
			className={cx("shrink-0", className)}
		>
			{title && <title>{title}</title>}
			<defs>
				<linearGradient id={gradient} x1="0" y1="0" x2="32" y2="32">
					<stop offset="0" stopColor="#0ba4ba" />
					<stop offset="0.55" stopColor="#0e697e" />
					<stop offset="1" stopColor="#134758" />
				</linearGradient>
			</defs>

			<rect width="32" height="32" rx="8.5" fill={`url(#${gradient})`} />
			{/* A single light pass across the top corner keeps the tile from
			    reading as a flat block at large sizes. */}
			<path
				d="M0 8.5A8.5 8.5 0 0 1 8.5 0H32L0 22Z"
				fill="#ffffff"
				fillOpacity="0.1"
			/>

			{/* Two settled lines, one struck through. */}
			<path
				d="M7.5 10.5h17M7.5 16h11.5"
				stroke="#cff8fb"
				strokeWidth="2.6"
				strokeLinecap="round"
			/>
			{/* The one still open. */}
			<path d="M7.5 21.5h6" stroke="#67dde9" strokeWidth="2.6" strokeLinecap="round" />
			<circle cx="21.5" cy="21.5" r="3.9" stroke="#67dde9" strokeWidth="2.4" />
		</svg>
	)
}

/* -------------------------------------------------------------------------- */
/* Wordmark                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * "Loomina", drawn.
 *
 * Monoline geometric construction on a 14-unit x-height: the round letters are
 * true circles and the shoulders are true semicircles, so the word holds its
 * rhythm when it is scaled down to a signature or up to a hero.
 *
 * The dot on the i is the one piece of colour - the smallest possible nod to
 * the open ring in the mark.
 */
export function LoominaWordmark({ height = 20, className, accent = "#0ba4ba" }) {
	return (
		<svg
			height={height}
			viewBox="0 0 138 30"
			fill="none"
			aria-hidden
			className={cx("shrink-0", className)}
			style={{ width: (height * 138) / 30 }}
		>
			<g
				stroke="currentColor"
				strokeWidth="2.9"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				{/* L */}
				<path d="M7 4v20h9" />
				{/* o o */}
				<circle cx="28" cy="17" r="7" />
				<circle cx="47" cy="17" r="7" />
				{/* m - stem, then two true semicircular shoulders */}
				<path d="M59 24V10M59 17a7 7 0 0 1 14 0v7M73 17a7 7 0 0 1 14 0v7" />
				{/* i */}
				<path d="M92 10v14" />
				{/* n */}
				<path d="M97 24V10M97 17a7 7 0 0 1 14 0v7" />
				{/* a - bowl plus its own stem */}
				<circle cx="123" cy="17" r="7" />
				<path d="M130 10v14" />
			</g>
			<circle cx="92" cy="5.2" r="1.85" fill={accent} />
		</svg>
	)
}

/* -------------------------------------------------------------------------- */
/* Lockups                                                                     */
/* -------------------------------------------------------------------------- */

/** Mark plus wordmark, the default signature. */
export function Logo({ size = "md", className, markOnly = false }) {
	const scale = {
		sm: { mark: 24, word: 15 },
		md: { mark: 30, word: 18 },
		lg: { mark: 40, word: 24 }
	}[size] || { mark: 30, word: 18 }

	return (
		<span className={cx("inline-flex items-center gap-2.5", className)}>
			<LoominaMark size={scale.mark} />
			{!markOnly && <LoominaWordmark height={scale.word} />}
		</span>
	)
}

/**
 * Kept for the places that imported the old name, so a rename does not have to
 * land in the same change as the artwork.
 */
export function Logomark({ size = "md", className }) {
	return <LoominaMark size={size === "lg" ? 40 : 28} className={className} />
}

/* -------------------------------------------------------------------------- */
/* Spinner                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The mark, waiting.
 *
 * The list writes itself one line at a time and the open ring turns - the
 * same glyph as the favicon rather than a generic circle, so a slow load still
 * looks like the product. SMIL rather than CSS keyframes because the identical
 * file is also served as `/spinner.svg`, where a stylesheet would not reach it.
 */
export function BrandSpinner({ size = 40, label = "Loading", className }) {
	return (
		<span
			role="status"
			aria-live="polite"
			className={cx("inline-flex flex-col items-center gap-2.5", className)}
		>
			<svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
				<defs>
					<linearGradient id="loomina-spin-tile" x1="0" y1="0" x2="32" y2="32">
						<stop offset="0" stopColor="#0ba4ba" />
						<stop offset="0.55" stopColor="#0e697e" />
						<stop offset="1" stopColor="#134758" />
					</linearGradient>
				</defs>

				<rect width="32" height="32" rx="8.5" fill="url(#loomina-spin-tile)" />
				<path
					d="M0 8.5A8.5 8.5 0 0 1 8.5 0H32L0 22Z"
					fill="#ffffff"
					fillOpacity="0.1"
				/>

				{[
					{ d: "M7.5 10.5h17", begin: "0s", colour: "#cff8fb" },
					{ d: "M7.5 16h11.5", begin: "0.25s", colour: "#cff8fb" },
					{ d: "M7.5 21.5h6", begin: "0.5s", colour: "#67dde9" }
				].map((line) => (
					<path
						key={line.d}
						d={line.d}
						stroke={line.colour}
						strokeWidth="2.6"
						strokeLinecap="round"
						opacity="0.25"
					>
						<animate
							attributeName="opacity"
							values="0.25;1;1;0.25"
							keyTimes="0;0.2;0.65;1"
							dur="1.5s"
							begin={line.begin}
							repeatCount="indefinite"
						/>
					</path>
				))}

				<circle
					cx="21.5"
					cy="21.5"
					r="3.9"
					stroke="#67dde9"
					strokeWidth="2.4"
					strokeLinecap="round"
					strokeDasharray="13 11"
				>
					<animateTransform
						attributeName="transform"
						type="rotate"
						from="0 21.5 21.5"
						to="360 21.5 21.5"
						dur="1.1s"
						repeatCount="indefinite"
					/>
				</circle>
			</svg>
			{label && <span className="text-[0.8125rem] text-ink-500">{label}</span>}
		</span>
	)
}
